import { DateTime } from '@ksyos/date-time';
import { Card, CollectionLink, Heading, Link, PageHeader, SectionHeader } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { IPreviousPayment } from '@common/interfaces';
import { CompensationPayoutLevel, PayoutRecipient } from '@common/enums';
import { Currency } from '@frontend/components/elements/common';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import { PayoutRecordDetailOverview } from './PayoutRecordDetailOverview';
import { UpcomingPaymentsOverview } from './UpcomingPaymentsOverview';

interface IPayoutsDetailsProps {
    backUrl: string;
    payoutLevel: CompensationPayoutLevel.employment | CompensationPayoutLevel.organization;
    payoutLevelEntityId: string;
    payoutLevelEntityRelationNumber: string;
}

export function PayoutsDetails(props: IPayoutsDetailsProps) {
    const abortableFetch = useAbortableFetch();
    const navigate = useNavigate();
    const [previousPayments, setPreviousPayments] = useState<IPreviousPayment[]>([]);
    const [selectedPreviousPayment, setSelectedPreviousPayment] = useState<IPreviousPayment | null>(null);
    const relationNumber = props.payoutLevelEntityRelationNumber;
    const translate = getSelectedLanguage();

    useEffect(() => {
        loadPreviousPayments();
    }, [relationNumber]);

    useEffect(() => {
        setSelectedPreviousPayment(null);
    }, [props.backUrl]);

    return !selectedPreviousPayment ? (
        <>
            <PageHeader title={translate.finance.payments} />
            <SectionHeader title={translate.finance.upcomingPayments} />
            <UpcomingPaymentsOverview
                payoutLevel={props.payoutLevel}
                payoutLevelEntityId={props.payoutLevelEntityId}
            />
            {renderPreviousPayments()}
        </>
    ) : (
        renderPreviousPaymentDetails()
    );

    async function loadPreviousPayments() {
        const payoutRecipient =
            props.payoutLevel === CompensationPayoutLevel.employment
                ? PayoutRecipient.practitioner
                : PayoutRecipient.organization;
        const url = `/api/payments/payoutRecordByRelationNumber?relationNumber=${relationNumber}&entityToPay=${payoutRecipient}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const orderedPreviousPayments = orderBy(
            result.json.items as IPreviousPayment[],
            [
                (previousPayment) => DateTime.parse(previousPayment.usedFilters.sentBeforeDate, 'DD-MM-YYYY').toDate(),
                (previousPayment) => previousPayment.runNumber,
            ],
            ['desc', 'desc']
        );

        setPreviousPayments(orderedPreviousPayments);
    }

    function renderPreviousPaymentDetails() {
        if (!selectedPreviousPayment) {
            return null;
        }

        let linkText = translate.finance.overviewPayouts;
        if (props.payoutLevel === CompensationPayoutLevel.employment) {
            linkText += `: ${selectedPreviousPayment.providerOrganizationName}`;
        }

        const payoutReference = `${
            translate.episodeCategoryEnum[
                selectedPreviousPayment.episodeCategory as keyof typeof translate.episodeCategoryEnum
            ]
        } ${DateTime.parse(selectedPreviousPayment.usedFilters.sentBeforeDate, 'DD-MM-YYYY').format('YYYYMMDD')}-R${
            selectedPreviousPayment.runNumber
        }-${selectedPreviousPayment.payoutReference}`;

        return (
            <>
                <Link
                    className="mb-4"
                    color="secondary"
                    iconLeft="caret-left"
                    onClick={() => {
                        navigate(props.backUrl);
                        setSelectedPreviousPayment(null);
                    }}
                    variant="button"
                >
                    {linkText}
                </Link>
                <Heading
                    mb={4}
                    size="xlarge"
                >{`${translate.finance.payout} ${payoutReference}`}</Heading>
                <PayoutRecordDetailOverview
                    amount={selectedPreviousPayment.amount}
                    bankAccountDetailsId={selectedPreviousPayment.bankAccountDetailsId}
                    organization={selectedPreviousPayment.providerOrganizationName}
                    payoutLevel={props.payoutLevel}
                    payoutLevelEntityId={props.payoutLevelEntityId}
                    payoutReference={payoutReference}
                    relationFileId={selectedPreviousPayment.relationFileId}
                    relationNumber={relationNumber}
                    sentBeforeDate={selectedPreviousPayment.usedFilters.sentBeforeDate}
                />
            </>
        );
    }

    function renderPreviousPayments() {
        if (previousPayments.length === 0) {
            return null;
        }

        return (
            <>
                <Heading
                    mb={4}
                    size="large"
                >
                    {translate.finance.previousPayments}
                </Heading>
                <Card>
                    {previousPayments.map((payment) => (
                        <CollectionLink
                            key={`${payment.usedFilters.sentBeforeDate}-R${payment.runNumber}-${payment.payoutReference}-${payment.amount}`}
                            onClick={() => setSelectedPreviousPayment(payment)}
                            subtitle={getSubtitle(payment)}
                            title={`${
                                translate.episodeCategoryEnum[
                                    payment.episodeCategory as keyof typeof translate.episodeCategoryEnum
                                ]
                            } ${DateTime.parse(payment.usedFilters.sentBeforeDate, 'DD-MM-YYYY').format('YYYYMMDD')}-R${
                                payment.runNumber
                            }-${payment.payoutReference}`}
                        >
                            <Currency value={payment.amount} />
                        </CollectionLink>
                    ))}
                </Card>
            </>
        );
    }

    function getSubtitle(payment: IPreviousPayment) {
        let subtitle = `${translate.common.until} ${DateTime.parse(payment.usedFilters.sentBeforeDate, 'DD-MM-YYYY')
            .endOf('quarter')
            .format('DD-MM-YYYY')}`;
        if (payment.usedFilters.serviceName) {
            subtitle += ` · ${payment.usedFilters.serviceName}`;
        }
        subtitle += ` · ${payment.providerOrganizationName}`;

        return subtitle;
    }
}
